import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Loader2, Undo2 } from 'lucide-react';
import AuthedImage from '@/components/AuthedImage';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';

/**
 * Re-shoot a photo on a contact that is already saved.
 *
 * Deliberately not the badge scanner. That reads the badge and fills the form
 * in, which is right at capture and wrong here: the rep has already corrected
 * those fields by hand, and a second read would quietly overwrite the
 * corrections with whatever the model saw this time. This only replaces the
 * picture and touches nothing else.
 *
 * The upload happens immediately, so the new photo is in R2 before Save is
 * pressed, and the key is what gets written on save. An abandoned upload
 * leaves one orphaned object in the bucket, which is a cheaper problem than
 * holding the bytes in memory while the rep edits the rest of the form.
 *
 * Compression matches BadgeScanner: 1800px longest edge, JPEG at 0.92. A
 * modern phone photo is 4 to 8 MB and none of that detail survives being read
 * on a 6 inch screen, but all of it has to cross convention centre wifi.
 */

const MAX_EDGE = 1800;

function compress(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_EDGE || height > MAX_EDGE) {
        if (width > height) { height = Math.round(height * MAX_EDGE / width); width = MAX_EDGE; }
        else { width = Math.round(width * MAX_EDGE / height); height = MAX_EDGE; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name || 'photo.jpg', { type: 'image/jpeg' }) : file),
        'image/jpeg',
        0.92,
      );
    };
    // A file the browser cannot decode is sent as it is rather than dropped.
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export default function ReplacePhotoField({ label, kind, value, original, onChange, disabled = false, onOpenLightbox }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const replaced = value !== original;

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { key } = await base44.integrations.Core.UploadFile({ file: await compress(file), kind });
      onChange(key);
      toast.success('New photo ready. Save to keep it.');
    } catch (err) {
      // The old key is left alone, so a failed upload changes nothing.
      toast.error(err.message || 'Could not upload that photo');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        {replaced && (
          <button
            type="button"
            onClick={() => onChange(original)}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
          >
            <Undo2 className="w-3 h-3" /> Undo
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 mt-1">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary/50 shrink-0">
          {value ? (
            // Tapping opens the same full-size lightbox Home's own contact
            // row already uses (task: photo lightbox on Edit Contact,
            // 2026-09-24), not this component's own second copy of it: the
            // open/closed state, and where the overlay itself renders, both
            // live one level up in EditContact.jsx, same as ContactRow.jsx
            // owning its own.
            <button
              type="button"
              onClick={() => onOpenLightbox?.(`/api/images/${value}`)}
              className="w-full h-full block"
              title={`View ${label.toLowerCase()} full size`}
            >
              <AuthedImage
                src={`/api/images/${value}`}
                alt={label}
                className="w-full h-full object-cover"
              />
            </button>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="w-5 h-5 text-muted-foreground/40" />
            </div>
          )}
        </div>

        <div className="min-w-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
            className="h-8 text-xs"
          >
            {busy
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading</>
              : <><Camera className="w-3.5 h-3.5 mr-1.5" />{value ? 'Replace' : 'Add photo'}</>}
          </Button>
          {replaced && <p className="text-[10px] text-accent mt-1">New photo, not saved yet</p>}
        </div>
      </div>

      {/* Opens a camera straight away on a phone rather than the photo
          library, which is what a re-shoot means. Which camera depends on
          what is being re-shot: the rear camera for a badge or card (same
          as BadgeScanner), the front camera for a photo of the person
          (same as PersonPhotoCapture during initial capture). This field
          used to hardcode "environment" for both, so re-shooting a person
          photo opened the wrong camera. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture={kind === 'person' ? 'user' : 'environment'}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
